import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Phone, Calendar, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [sidebarSubmitted, setSidebarSubmitted] = useState(false);
  const faqs = [{
    question: 'Do you charge a call-out fee?',
    answer: "We provide upfront pricing before any work begins. Contact us for a quote and we'll give you a clear, honest price with no hidden extras."
  }, {
    question: 'Are you available for after-hours emergencies?',
    answer: "Yes. We provide 24/7 emergency callout across Perth's south. Call 0418 381 988 for immediate assistance."
  }, {
    question: 'How quickly can you get to me?',
    answer: 'For emergencies, we respond as fast as possible. For standard jobs, most are quoted promptly and completed same-day where possible.'
  }, {
    question: 'Do you offer a warranty on your work?',
    answer: "Yes. All work is backed by the Completely Electrical Guarantee — quality workmanship, every time. If you're not satisfied, we'll make it right."
  }, {
    question: 'What areas of Perth do you service?',
    answer: "We're based in Huntingdale and service the entire SOR region. Priority areas include Gosnells, Thornlie, Maddington, Southern River, Harrisdale, Leeming, and Cockburn. We also regularly service Mandurah and Golden Bay."
  }, {
    question: 'Can you upgrade the lighting in my newly built home?',
    answer: 'Absolutely — this is one of our specialties. Many new homeowners want better lighting than the builder-standard fittings. We install premium pendants, LED strip lighting, feature lighting, and downlight upgrades to transform your new home.'
  }, {
    question: 'Do you install split system air conditioners?',
    answer: "Yes. We supply, install, and replace split system air conditioners for both residential and commercial properties across Perth's south."
  }, {
    question: 'Do you do commercial electrical work?',
    answer: 'Yes. We work with builders, transportable builders, mechanics, shed builders, and real estate agencies across Kenwick, Maddington, and surrounding areas. We handle commercial lighting, power, and fit-outs.'
  }];
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-20 lg:py-28 bg-white" id="faq" data-id="element-52">
      <div className="max-w-7xl mx-auto px-4" data-id="element-53">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-54">
          <span className="text-orange font-bold tracking-wider uppercase text-sm mb-3 block" data-id="element-55">
            Common Questions
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-navy mb-6 tracking-tighter" data-id="element-56">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate leading-relaxed" data-id="element-57">
            Clear, honest answers to help you make a confident decision.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start" data-id="element-58">
          <div className="lg:col-span-8 space-y-3" data-id="element-59">
            {faqs.map((faq, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: index * 0.04
          }} className={`bg-white border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 ${openIndex === index ? 'border-orange ring-1 ring-orange/20' : 'border-gray-200'}`} data-id="element-60">
                <button className="w-full px-7 py-5 text-left flex justify-between items-center focus:outline-none" onClick={() => toggleFAQ(index)} data-id="element-61">
                  <span className="font-heading font-extrabold text-navy text-base pr-8" data-id="element-62">
                    {faq.question}
                  </span>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openIndex === index ? 'bg-orange/10' : 'bg-gray-50'}`} data-id="element-63">
                    {openIndex === index ? <ChevronUp className="w-5 h-5 text-orange" data-id="element-64" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-65" />}
                  </div>
                </button>

                <AnimatePresence data-id="element-66">
                  {openIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-67">
                      <div className="px-7 pb-6 text-slate text-base leading-relaxed border-t border-gray-50 pt-4" data-id="element-68">
                        {faq.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>

          {/* Sticky CTA Sidebar */}
          <div className="lg:col-span-4 lg:sticky lg:top-32" data-id="element-69">
            <div className="bg-navy rounded-2xl p-7 shadow-2xl border border-white/10 relative overflow-hidden" data-id="element-70">
              <div className="absolute top-0 right-0 w-32 h-32 bg-orange/20 blur-2xl rounded-full" data-id="element-71"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 blur-2xl rounded-full" data-id="element-72"></div>

              <div className="relative z-10" data-id="element-73">
                <AnimatePresence mode="wait" data-id="element-74">
                  {!sidebarSubmitted ? <motion.div key="sidebar-form" initial={{
                  opacity: 1
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.3
                }} data-id="element-75">
                      <h3 className="text-2xl font-heading font-extrabold text-white mb-3" data-id="element-76">
                        Still Have Questions?
                      </h3>
                      <p className="text-gray-200 mb-6 text-base" data-id="element-77">
                        Our team is ready to help — no obligation, no pressure.
                      </p>

                      <a href="tel:0418381988" className="w-full bg-orange hover:bg-orange-600 text-white px-6 py-4 rounded-xl font-extrabold text-lg flex items-center justify-center gap-3 transition-all shadow-lg hover:-translate-y-1 mb-4" data-id="element-78">
                        <Phone className="w-5 h-5" data-id="element-79" />
                        Call 0418 381 988
                      </a>

                      <div className="flex items-center gap-4 my-5" data-id="element-80">
                        <div className="h-px bg-white/20 flex-1" data-id="element-81"></div>
                        <span className="text-gray-300 font-bold text-sm uppercase" data-id="element-82">
                          OR
                        </span>
                        <div className="h-px bg-white/20 flex-1" data-id="element-83"></div>
                      </div>

                      <form className="space-y-3" onSubmit={e => {
                    e.preventDefault();
                    setSidebarSubmitted(true);
                  }} data-id="element-84">
                        <input type="text" placeholder="Your Name" className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:border-orange focus:ring-1 focus:ring-orange outline-none transition-all font-medium text-sm" data-id="element-85" />
                        <input type="tel" placeholder="Phone Number" className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:border-orange focus:ring-1 focus:ring-orange outline-none transition-all font-medium text-sm" data-id="element-86" />
                        <button type="submit" className="w-full bg-white text-navy hover:bg-gray-50 font-extrabold py-3.5 rounded-xl transition-all text-base flex items-center justify-center gap-2" data-id="element-87">
                          <Calendar className="w-5 h-5" data-id="element-88" />
                          Request Callback
                        </button>
                      </form>
                    </motion.div> : <motion.div key="sidebar-confirmation" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.4
                }} className="text-center py-4" data-id="element-89">
                      <div className="w-14 h-14 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-90">
                        <CheckCircle className="w-7 h-7 text-emerald-400" data-id="element-91" />
                      </div>
                      <h3 className="text-xl font-heading font-extrabold text-white mb-2" data-id="element-92">
                        Request Received!
                      </h3>
                      <p className="text-gray-300 text-sm mb-4 leading-relaxed" data-id="element-93">
                        Brodie will text you within{' '}
                        <strong className="text-white" data-id="element-94">15 minutes</strong> to
                        help with your question.
                      </p>
                      <a href="tel:0418381988" className="inline-flex items-center gap-2 text-orange font-bold text-sm hover:text-orange-300 transition-colors" data-id="element-95">
                        <Phone className="w-4 h-4" data-id="element-96" />
                        Need us sooner? Call 0418 381 988
                      </a>
                      <button onClick={() => setSidebarSubmitted(false)} className="block mx-auto mt-3 text-xs text-gray-400 hover:text-white transition-colors" data-id="element-97">
                        Submit another request
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}